import { Link } from "react-router-dom";
import { ShoppingCart, Trash2, ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCart } from "../contexts/CartContext";

const CartPage = () => {
  const { items, removeFromCart, clearCart } = useCart();

  const total = items.reduce((sum, item) => {
    const amount = Number(item.amount);
    return Number.isFinite(amount) ? sum + amount : sum;
  }, 0);

  const commonCurrency = items.length
    ? items.every((item) => item.currency === items[0].currency)
      ? items[0].currency
      : null
    : null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="container mx-auto px-4 py-8">
        <Link
          to="/profile"
          className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to profile
        </Link>

        <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">
              Your Cart
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {items.length} {items.length === 1 ? "item" : "items"} saved for later.
            </p>
          </div>

          {items.length > 0 && (
            <button
              type="button"
              onClick={clearCart}
              className="rounded-lg border border-border bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-muted"
            >
              Clear cart
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="rounded-xl border border-border bg-card px-6 py-14 text-center">
            <ShoppingCart className="mx-auto h-10 w-10 text-muted-foreground" />
            <h2 className="mt-4 text-lg font-semibold text-foreground">
              Your cart is empty
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Add items while browsing and they will appear here.
            </p>
            <Link
              to="/"
              className="mt-5 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
            >
              Browse items
            </Link>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={item.itemId}
                  className="flex gap-4 rounded-xl border border-border bg-card p-4"
                >
                  <Link
                    to={`/item/${item.itemId}`}
                    className="h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-secondary"
                  >
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.itemName}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : null}
                  </Link>

                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/item/${item.itemId}`}
                      className="font-heading text-base font-semibold text-foreground hover:text-primary"
                    >
                      {item.itemName}
                    </Link>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                      {item.description || "No description"}
                    </p>
                    <p className="mt-2 font-semibold text-primary">
                      {item.currency} {Number(item.amount || 0).toLocaleString()}
                    </p>
                    {(item.locCity || item.locCountry) && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {[item.locCity, item.locCountry].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => removeFromCart(item.itemId)}
                    aria-label={`Remove ${item.itemName} from cart`}
                    className="h-9 w-9 shrink-0 rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
                  >
                    <Trash2 className="mx-auto h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <aside className="h-fit rounded-xl border border-border bg-card p-5">
              <h2 className="font-heading text-lg font-semibold text-foreground">
                Cart summary
              </h2>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Items</span>
                <span className="font-medium text-foreground">{items.length}</span>
              </div>
              {commonCurrency ? (
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Combined value</span>
                  <span className="font-semibold text-primary">
                    {commonCurrency} {total.toLocaleString()}
                  </span>
                </div>
              ) : (
                <p className="mt-3 text-xs text-muted-foreground">
                  Items use different currencies, so a combined value is not shown.
                </p>
              )}
              <p className="mt-4 text-xs leading-5 text-muted-foreground">
                The cart saves items for easy access. Contact each seller from the item page to complete a deal.
              </p>
            </aside>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CartPage;
